import * as React from 'react';
import { t } from '@citrite/translate';
import { Button } from '@citrite/web-ui-component';
import { WorkspaceConfiguration } from '@citrite/workspace-ui-platform';
import $ from 'jquery';
import {
	getAuthMethodErrorText,
	getAuthMethodLogonText,
	getSwitchToAuthMethodText,
} from 'Login/DSAuth/authMethodUtil';
import { NextLogonMethodButton } from 'Login/DSAuth/nextLogonMethodButton';
import { AuthScreen, InformationMessage } from 'Login/Login';
import {
	allowReloginWithoutBrowserClose,
	authenticate,
	clearSavedAuthenticationMethod,
	getAuthenticationMethods,
	getSavedAuthenticationMethod,
	isBrowserCloseRequiredToEndSession,
	isSmartCardSession,
	Method,
	parseChallenge,
	setIdpLogoffUrl,
	setPasswordChangeAllowed,
	setSavedAuthenticationMethod,
} from './authentication';
import { ChangePasswordController } from './changePasswordController';
import { ExplicitAuthController, NextLogonMethodDetails } from './explicitAuthController';
import { AuthenticationResult } from './formsAuthentication';

const defaultOptions = {
	onComplete() {},
	onError(_failureId?: string) {},
};

export interface AuthenticationMethod {
	name: string;
	url: string;
}

export class AuthenticationController {
	private options: typeof defaultOptions;
	private changePasswordController: ChangePasswordController;
	private explicitAuthController: ExplicitAuthController;
	private isOnPrem: boolean = IS_ON_PREM;
	private authenticationMethods: AuthenticationMethod[] = [];

	public constructor(
		private $explicitAuthView: JQuery,
		private $passwordExpiryView: JQuery,
		private $onPremCommonAuthView: JQuery,
		options: Partial<typeof defaultOptions>,
		private workspaceConfiguration: WorkspaceConfiguration,
		private showForms: (authScreen?: AuthScreen) => void,
		private showMessage: (msg: InformationMessage) => void,
		private routedParams: string
	) {
		this.options = { ...defaultOptions, ...options };
		this.workspaceConfiguration = workspaceConfiguration;
		this.changePasswordController = new ChangePasswordController(
			this.$passwordExpiryView,
			this.options,
			this.workspaceConfiguration,
			showForms
		);

		$.fn.ctxsDisplayPane = function () {
			$('.fullscreen-pane').hide();
			this.show();
			return this;
		};
	}

	public authenticate(challengeString: string) {
		const challenge = parseChallenge(challengeString);

		if (challenge.mode === 'Redirect') {
			window.location.href = challenge.location;
			return;
		}

		// After a successful smart card or webview (e.g. SAML) logon, subsequent logins are blocked until the user closes the browser.
		// This is done for security reasons because the browser may cache the PIN, or an authentication token.
		if (isBrowserCloseRequiredToEndSession() && !allowReloginWithoutBrowserClose()) {
			this.showMessage({
				title: isSmartCardSession()
					? t('Workspace:login.smartcard_auth_failure')
					: t('Workspace:login.auth_failure'),
				content: <p className="detail-text">{t('Workspace:login.close_browser')}</p>,
			});
		}
		if (this.isOnPrem) {
			getAuthenticationMethods(
				challenge.location,
				this.workspaceConfiguration,
				this.processAuthenticationMethods,
				this.handleGetAuthMethodsFailure
			);
		} else {
			this.performAuthentication({
				name: Method.EXPLICIT,
				url: this.workspaceConfiguration.authManager.logonURL || 'ExplicitAuth/Login',
			});
		}
	}

	private processAuthenticationMethods = (
		authenticationMethods: AuthenticationMethod[]
	) => {
		this.authenticationMethods = authenticationMethods;
		if (authenticationMethods.length === 0) {
			this.showMessage({
				title: t('Workspace:login.no_available_auth_methods'),
			});
		} else if (authenticationMethods.length === 1) {
			this.performAuthentication(authenticationMethods[0]);
		} else {
			const savedAuthMethod = getSavedAuthenticationMethod();

			if (savedAuthMethod) {
				this.performAuthentication(savedAuthMethod, true);
			} else {
				this.showAuthPrompt(authenticationMethods[0], true);
			}
		}
	};

	private performAuthentication(
		authenticationMethod: AuthenticationMethod,
		hasMultipleAuthMethods = false
	) {
		const self = this;
		if (
			authenticationMethod.name === Method.EXPLICIT ||
			authenticationMethod.name === Method.GENERIC_FORMS
		) {
			if (!this.explicitAuthController) {
				const nextAuthMethodDetails: NextLogonMethodDetails = hasMultipleAuthMethods
					? {
							switchToNextLoginMethod: () => {
								this.showAuthPrompt(this.findNextAuthMethod(authenticationMethod), false);
							},
					  }
					: undefined;

				this.explicitAuthController = new ExplicitAuthController(
					this.$explicitAuthView,
					{
						prepopulatedCredentials: this.workspaceConfiguration.prePopulatedCredentials,
						onAuthenticationSuccess(authInfo) {
							self.completeAuthentication(
								authInfo,
								hasMultipleAuthMethods,
								authenticationMethod
							);
						},
						onLogonTimeout: $.proxy(this.handleLogonTimeout, this),
						onAuthenticationError: this.options.onError,
					},
					this.workspaceConfiguration,
					this.showForms,
					this.routedParams,
					nextAuthMethodDetails
				);
			}

			this.explicitAuthController.authenticate(authenticationMethod.url);
		} else if (
			authenticationMethod.name === Method.GATEWAY ||
			authenticationMethod.name === Method.SMARTCARD ||
			authenticationMethod.name === Method.PASSTHROUGH
		) {
			authenticate(
				authenticationMethod.url,
				function (authInfo) {
					self.completeAuthentication(
						authInfo,
						hasMultipleAuthMethods,
						authenticationMethod
					);
				},
				function () {
					self.handleAuthenticationFailure(authenticationMethod);
				}
			);
		}
	}

	private showAuthPrompt(authenticationMethod: AuthenticationMethod, isError: boolean) {
		const nextAuthMethod = this.findNextAuthMethod(authenticationMethod);
		const hasMultipleAuth = this.authenticationMethods.length > 1;

		this.showForms({
			content: (
				<div>
					<h1 className="authentication-title main-text">
						{isError
							? getAuthMethodErrorText(authenticationMethod)
							: getAuthMethodLogonText(authenticationMethod)}
					</h1>
					{!isError && (
						<Button.Wrapper
							id="btnLogon"
							className="stacked-wrapper"
							onClick={() =>
								this.performAuthentication(authenticationMethod, hasMultipleAuth)
							}
						>
							<div className="default stacked button">{t('Workspace:logon')}</div>
						</Button.Wrapper>
					)}
					<NextLogonMethodButton
						onClick={() => this.performAuthentication(nextAuthMethod, hasMultipleAuth)}
					>
						{hasMultipleAuth
							? getSwitchToAuthMethodText(nextAuthMethod)
							: t('Workspace:login.try_again')}
					</NextLogonMethodButton>
				</div>
			),
		});
		this.$onPremCommonAuthView.ctxsDisplayPane();
	}

	public findNextAuthMethod(
		currentAuthMethod: AuthenticationMethod
	): AuthenticationMethod {
		let nextAuthMethodIndex = 0;
		for (let i = 0; i < this.authenticationMethods.length; i++) {
			if (this.authenticationMethods[i].name === currentAuthMethod.name) {
				nextAuthMethodIndex = i + 1;
				if (nextAuthMethodIndex >= this.authenticationMethods.length) {
					nextAuthMethodIndex = 0;
				}
			}
		}

		return this.authenticationMethods[nextAuthMethodIndex];
	}

	private handleAuthenticationFailure = (authenticationMethod: AuthenticationMethod) => {
		clearSavedAuthenticationMethod();

		if (authenticationMethod.name === Method.GATEWAY) {
			this.showMessage({
				title: t('Workspace:login.auth_failure'),
			});
			return;
		}

		switch (authenticationMethod.name) {
			case Method.SMARTCARD:
			case Method.PASSTHROUGH:
				this.showAuthPrompt(authenticationMethod, true);
				break;
		}
	};

	private handleGetAuthMethodsFailure = () => {
		this.showMessage({
			title: t('Workspace:login.auth_failure'),
			content: (
				<Button.Wrapper
					className="try_again"
					type="button"
					onClick={() => location.reload()}
				>
					{t('Workspace:login.try_again')}
				</Button.Wrapper>
			),
		});
	};

	private handleLogonTimeout() {
		this.showMessage({
			title: t('Workspace:login.timed_out'),
			content: (
				<Button.Wrapper
					className="default button"
					type="button"
					onClick={() => location.reload()}
				>
					{t('Workspace:login.log_on')}
				</Button.Wrapper>
			),
		});
	}

	private completeAuthentication(
		authInfo: AuthenticationResult,
		isMultipleAuthMethod: boolean,
		authenticationMethod: AuthenticationMethod
	) {
		if (IS_ON_PREM && isMultipleAuthMethod) {
			setSavedAuthenticationMethod(authenticationMethod);
		}
		setPasswordChangeAllowed(authInfo.isPasswordChangeAllowed);
		if (!!authInfo.webLogoffUrl) {
			setIdpLogoffUrl(authInfo.webLogoffUrl);
		}

		if (authInfo.isPasswordExpiryNotificationEnabled) {
			this.showForms();
			this.$passwordExpiryView.ctxsDisplayPane();
			this.changePasswordController.showPasswordExpiryMessage(
				authInfo.passwordExpiryTime
			);
		} else {
			this.options.onComplete();
		}
	}
}
